import type { RouteRecordRaw } from 'vue-router'

// 顶层鉴权路由的权限标识枚举。
// 新增需要受路由权限控制的一级菜单时，需要同时补充这里和 routeAccessMap。
export const ROUTE_ACCESS_KEYS = [
  'home',
  'commission',
  'order',
  'customer',
  'inventory',
  'reports',
  'teams',
  'system',
] as const

export type RouteAccessKey = (typeof ROUTE_ACCESS_KEYS)[number]

export interface RouteAccessContext {
  userId: string
  superAdmin: boolean
  hiddenCommissionModule: boolean
  isBuHeaderView: boolean
}

export interface RouteAccessRule {
  // 是否需要“超级管理员或特例账号”才能访问
  requireElevatedAccess?: boolean
  // 当前处于 BU 视图时禁止访问
  denyInBuView?: boolean
  // 当前账号组合下需要隐藏佣金模块时禁止访问
  denyWhenCommissionModuleHidden?: boolean
  // 仅允许在 BU 视图下访问
  requireBuView?: boolean
}

// 当前项目里存在一小部分历史特例账号，菜单逻辑此前已用硬编码放行。
// 这里把规则集中到路由权限层，避免菜单显示与路由进入出现分叉。
export const ELEVATED_ROUTE_USER_IDS = ['53zBsXmYAwd'] as const

// 路由权限规则的单一事实来源：
// - key 与顶层受保护路由的 meta.accessKey 一一对应
// - 菜单过滤、路由守卫都应复用这里，避免多处分散判断
export const routeAccessMap = {
  home: {},
  commission: {
    denyInBuView: true,
    denyWhenCommissionModuleHidden: true,
  },
  order: {},
  customer: {},
  inventory: {},
  reports: {
    requireBuView: true,
  },
  teams: {
    requireElevatedAccess: true,
  },
  system: {
    requireElevatedAccess: true,
  },
} as const satisfies Record<RouteAccessKey, RouteAccessRule>

// 给顶层路由声明 accessKey。
// 子路由默认通过 matched 链继承父级 accessKey，只有需要单独覆盖时才额外声明。
export function withAccessKeyMeta<T extends NonNullable<RouteRecordRaw['meta']>>(
  accessKey: RouteAccessKey,
  meta: T,
): T & { accessKey: RouteAccessKey } {
  return {
    ...meta,
    accessKey,
  }
}
